import { resolveBackend } from "../storage/resolveBackend";
import type { PersistedEnvelope, PersistedSliceDescriptor } from "../types";
import { hasRetainedFuturePayload } from "./unknownPayloadKeeper";

/** Delay before persisting a slice after the last in-memory mutation. */
const DEBOUNCE_MS = 200;

/** Active debounce timers keyed by slice storage key. */
const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Latest envelope waiting to be flushed per slice key. */
const pendingEnvelopes = new Map<string, PersistedEnvelope<unknown>>();

/** Routes pending writes to the correct backend implementation. */
const envelopeBackendByKey = new Map<string, PersistedSliceDescriptor<unknown>["storage"]>();

/**
 * Writes the pending envelope for one slice if writes are allowed.
 *
 * @param sliceKey Slice storage key.
 */
const flushSlice = async( sliceKey: string ): Promise<void> => {
    const envelope = pendingEnvelopes.get( sliceKey );
    if ( envelope === undefined ) {
        return;
    }

    pendingEnvelopes.delete( sliceKey );

    // Do not overwrite disk when a newer-version payload is retained for rollback safety.
    if ( hasRetainedFuturePayload( sliceKey ) ) {
        return;
    }

    const backendKind = envelopeBackendByKey.get( sliceKey );
    if ( backendKind === undefined ) {
        return;
    }

    const backend = resolveBackend( backendKind );
    await backend.set( sliceKey, envelope );
};

/**
 * Registers slice backend kind for flush routing.
 *
 * @typeParam TValue Slice value type.
 * @param slice Slice descriptor.
 */
export const registerSliceForWrites = <TValue>( slice: PersistedSliceDescriptor<TValue> ): void => {
    envelopeBackendByKey.set( slice.key, slice.storage );
};

/**
 * Schedules a debounced write for a slice envelope.
 *
 * @typeParam TValue Slice value type.
 * @param slice Slice descriptor.
 * @param envelope Envelope to persist at `slice.currentVersion`.
 */
export const scheduleSliceWrite = <TValue>(
    slice: PersistedSliceDescriptor<TValue>,
    envelope: PersistedEnvelope<TValue>,
): void => {
    registerSliceForWrites( slice );
    pendingEnvelopes.set( slice.key, envelope as PersistedEnvelope<unknown> );

    const existingTimer = pendingTimers.get( slice.key );
    if ( existingTimer !== undefined ) {
        clearTimeout( existingTimer );
    }

    pendingTimers.set(
        slice.key,
        setTimeout(

            /**
             * Debounced flush: runs after {@link DEBOUNCE_MS} without further mutations.
             */
            () => {
                pendingTimers.delete( slice.key );
                void flushSlice( slice.key );
            },
            DEBOUNCE_MS,
        ),
    );
};

/**
 * Flushes pending writes immediately for one slice.
 *
 * @param sliceKey Slice storage key.
 */
export const flushSliceWriteSync = async( sliceKey: string ): Promise<void> => {
    const timer = pendingTimers.get( sliceKey );
    if ( timer !== undefined ) {
        clearTimeout( timer );
        pendingTimers.delete( sliceKey );
    }

    await flushSlice( sliceKey );
};

/**
 * Flushes all pending slice writes (used on tab hide / unload).
 */
export const flushAllSliceWrites = async(): Promise<void> => {
    const keys = [ ...pendingEnvelopes.keys() ];
    for ( const key of keys ) {
        await flushSliceWriteSync( key );
    }
};

/**
 * Persists pending slices when the page is hidden or unloaded.
 */
const handlePageHide = (): void => {
    void flushAllSliceWrites();
};

if ( typeof window !== "undefined" ) {
    window.addEventListener( "pagehide", handlePageHide );
    window.addEventListener( "beforeunload", handlePageHide );
    document.addEventListener(
        "visibilitychange",

        /**
         * Flushes pending writes when the tab becomes hidden (mobile backgrounding).
         */
        () => {
            if ( document.visibilityState === "hidden" ) {
                void flushAllSliceWrites();
            }
        },
    );
}

/**
 * Builds an envelope for persistence at the current slice version.
 *
 * @typeParam TValue Slice value type.
 * @param slice Slice descriptor.
 * @param value Decoded in-memory value.
 * @returns Envelope ready for backend write.
 */
export const buildEnvelope = <TValue>(
    slice: PersistedSliceDescriptor<TValue>,
    value: TValue,
): PersistedEnvelope<TValue> => {
    return {
        "version":   slice.currentVersion,
        "data":      value,
        "updatedAt": Date.now(),
    };
};

/**
 * Whether a write would be skipped because a future payload is retained.
 *
 * @param sliceKey Slice storage key.
 * @returns `true` when disk write would be suppressed.
 * @internal Exported for unit tests.
 */
export const wouldSkipWriteForFuturePayload = ( sliceKey: string ): boolean => {
    return hasRetainedFuturePayload( sliceKey );
};
